import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CachedIcon from "@mui/icons-material/Cached";
import { useEffect } from "react";
import ApiEndpoints from "../../network/ApiEndPoints";
import { get, postJsonData, deleteRequest } from "../../network/ApiController";
import { apiErrorToast, okSuccessToast } from "../../utils/ToastUtil";
import Spinner from "../../component/loading-screen/Spinner";
import ModalHeader from "../../modals/ModalHeader";

const SalaryComponents = () => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: "" });

  // Form state
  const [formData, setFormData] = useState({
    component_name: "",
    type: "earning",
    calculation_type: "fixed",
    value: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchComponents = async () => {
    setLoading(true);
    get(
      ApiEndpoints.GET_PAYROLL_COMPONENT,
      "",
      setLoading,
      (res) => {
        setComponents(res.data.data || []);
        setLoading(false);
      },
      (error) => {
        apiErrorToast(error);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    fetchComponents();
  }, []);

  const handleOpenModal = (component = null) => {
    if (component) {
      setEditMode(true);
      setSelectedComponent(component);
      setFormData({
        component_name: component.component_name,
        type: component.type,
        calculation_type: component.calculation_type,
        value: component.value,
      });
    } else {
      setEditMode(false);
      setFormData({
        component_name: "",
        type: "earning",
        calculation_type: "fixed",
        value: "",
      });
    }
    setOpenModal(true);
    setFormErrors({});
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditMode(false);
    setSelectedComponent(null);
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.component_name.trim()) {
      errors.component_name = "Component name is required";
    }
    if (!formData.type) {
      errors.type = "Type is required";
    }
    if (!formData.calculation_type) {
      errors.calculation_type = "Calculation type is required";
    }
    if (!formData.value) {
      errors.value = "Value is required";
    } else if (isNaN(formData.value) || parseFloat(formData.value) < 0) {
      errors.value = "Value must be a positive number";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    const payload = {
      ...formData,
      value: parseFloat(formData.value),
    };

    const apiCall = editMode
      ? postJsonData(
          `${ApiEndpoints.UPDATE_PAYROLL_COMPONENT.replace("{id}", selectedComponent.id)}`,
          payload,
          setLoading,
          handleSuccess,
          handleError
        )
      : postJsonData(
          ApiEndpoints.CREATE_PAYROLL_COMPONENT,
          payload,
          setLoading,
          handleSuccess,
          handleError
        );
  };

  const handleSuccess = (res) => {
    okSuccessToast(res.data.message || `Component ${editMode ? "updated" : "created"} successfully`);
    handleCloseModal();
    fetchComponents();
  };

  const handleError = (error) => {
    apiErrorToast(error.message || `Failed to ${editMode ? "update" : "create"} component`);
    setLoading(false);
  };

  const handleDeleteClick = (id, name) => {
    setDeleteDialog({ open: true, id, name });
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    postJsonData(
      `${ApiEndpoints.DELETE_PAYROLL_COMPONENT.replace("{id}", deleteDialog.id)}`,
      setLoading,
      () => {
        okSuccessToast("Component deleted successfully");
        setDeleteDialog({ open: false, id: null, name: "" });
        fetchComponents();
      },
      (error) => {
        apiErrorToast(error.message || "Failed to delete component");
        setLoading(false);
        setDeleteDialog({ open: false, id: null, name: "" });
      }
    );
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null, name: "" });
  };

  const getTypeColor = (type) => {
    return type === "earning" ? "success" : "error";
  };

  const getCalculationTypeLabel = (type) => {
    return type === "percentage" ? "% of CTC" : "Fixed Amount";
  };

  return (
    <Box sx={{ p: 2 }}>
      <Spinner loading={loading} />

      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h6">Salary Components</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => handleOpenModal()}
            size="small"
            sx={{ backgroundColor: "#2F3B57" }}
          >
            Add Component
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchComponents} sx={{ color: "#0F52BA" }}>
              <CachedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Components Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell><strong>Component Name</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Calculation</strong></TableCell>
              <TableCell><strong>Value</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {components.length > 0 ? (
              components.map((comp) => (
                <TableRow key={comp.id} hover>
                  <TableCell>{comp.component_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={comp.type}
                      color={getTypeColor(comp.type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getCalculationTypeLabel(comp.calculation_type)}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {comp.calculation_type === "percentage" 
                      ? `${comp.value}%` 
                      : `₹${comp.value.toLocaleString('en-IN')}`}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenModal(comp)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(comp.id, comp.component_name)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">
                    No components found. Click "Add Component" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Component Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? "Edit Component" : "Add New Component"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Component Name"
                  name="component_name"
                  value={formData.component_name}
                  onChange={handleInputChange}
                  error={!!formErrors.component_name}
                  helperText={formErrors.component_name}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" error={!!formErrors.type}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    label="Type"
                  >
                    <MenuItem value="earning">Earning</MenuItem>
                    <MenuItem value="deduction">Deduction</MenuItem>
                  </Select>
                  {formErrors.type && (
                    <FormHelperText>{formErrors.type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" error={!!formErrors.calculation_type}>
                  <InputLabel>Calculation Type</InputLabel>
                  <Select
                    name="calculation_type"
                    value={formData.calculation_type}
                    onChange={handleInputChange}
                    label="Calculation Type"
                  >
                    <MenuItem value="fixed">Fixed Amount</MenuItem>
                    <MenuItem value="percentage">Percentage of CTC</MenuItem>
                  </Select>
                  {formErrors.calculation_type && (
                    <FormHelperText>{formErrors.calculation_type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={formData.calculation_type === "percentage" ? "Percentage (%)" : "Amount (₹)"}
                  name="value"
                  type="number"
                  value={formData.value}
                  onChange={handleInputChange}
                  error={!!formErrors.value}
                  helperText={formErrors.value}
                  size="small"
                  required
                  InputProps={{
                    startAdornment: formData.calculation_type === "fixed" ? "₹" : "",
                    endAdornment: formData.calculation_type === "percentage" ? "%" : "",
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {editMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Component</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteDialog.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalaryComponents;